import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { useComparison } from "@/hooks/use-comparisons";

interface WorkspaceHeaderProps {
    claimId?: number;
    claimNumber?: string;
    comparisonId: number;
    isProcessing?: boolean;
    onNavigateBack: () => void;
}

export const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
    claimNumber,
    comparisonId,
    isProcessing,
    onNavigateBack
}) => {
    const { toast } = useToast();
    const [isExporting, setIsExporting] = useState(false);
    
    // Fetch comparison data to get the comparison number
    const { data: comparisonData } = useComparison(comparisonId);

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const blob = await apiClient.comparisons.exportComparisonExcel({ id: comparisonId });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `comparison-${comparisonId}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Export successful",
                description: "Excel report has been downloaded.",
            });
        } catch (error) {
            console.error('Export failed:', error);
            toast({
                title: "Export failed",
                description: "Failed to download Excel report.",
                variant: "destructive",
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="flex items-center justify-between max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
            <div className="flex items-center gap-3 max-sm:min-w-0">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onNavigateBack}
                    className="text-muted-foreground hover:text-foreground h-7 w-7"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="max-sm:min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-base font-bold text-foreground leading-tight max-sm:truncate">Comparison Workspace</h1>
                        {isProcessing && <Badge variant="secondary" className="h-4 py-0 text-[9px] bg-amber-100 text-amber-800 border-amber-200">Processing</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-muted-foreground uppercase font-bold tracking-wider max-sm:flex-wrap max-sm:gap-1.5">
                        <span>Claim #{claimNumber}</span>
                        <span>•</span>
                        <span>{comparisonData?.comparison?.name || `Comparison #${comparisonId}`}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 max-sm:w-full">
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-2 text-xs max-sm:w-full"
                    onClick={handleExport}
                    disabled={isExporting}
                >
                    {isExporting ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                    )}
                    Export Excel
                </Button>
            </div>
        </div>
    );
};
